/**
 * Helper functions for handling consolidated schema fields.
 * These functions handle both JSON fields and legacy relations during migration.
 */

// ============================================================================
// Product Helpers
// ============================================================================

/**
 * Get product media from either JSON field or legacy relation
 */
export function getProductMedia(product: {
  media?: any;
  productMedia?: Array<{
    id?: string;
    type: string;
    url: string;
    alt?: string | null;
    position: number;
  }>;
}): Array<{
  id?: string;
  type: 'image' | 'video';
  url: string;
  alt?: string | null;
  position: number;
}> {
  // Prefer JSON field if available
  if (product.media && Array.isArray(product.media)) {
    return (product.media as any[]).sort((a, b) => a.position - b.position);
  }
  // Fallback to legacy relation
  if (product.productMedia && Array.isArray(product.productMedia)) {
    return product.productMedia
      .map(m => ({
        id: m.id,
        type: m.type as 'image' | 'video',
        url: m.url,
        alt: m.alt ?? undefined,
        position: m.position,
      }))
      .sort((a, b) => a.position - b.position);
  }
  return [];
}

/**
 * Get a single product price for a specific store location (or first available).
 */
export function getProductPrice(
  availabilities?: Array<{
    covaLocationId: number;
    price?: number | null;
    unitCost?: number | null;
  }>,
  storeCovaLocationId?: number
): number | null {
  if (!availabilities || !Array.isArray(availabilities) || availabilities.length === 0) {
    return null;
  }

  // If store location specified, find matching availability
  if (storeCovaLocationId) {
    const avail = availabilities.find(a => a.covaLocationId === storeCovaLocationId);
    if (avail) {
      return avail.price !== null && avail.price !== undefined
        ? Number(avail.price)
        : (avail.unitCost !== null && avail.unitCost !== undefined ? Number(avail.unitCost) : null);
    }
  }

  // Otherwise, use first available price
  const firstAvail = availabilities[0];
  return firstAvail.price !== null && firstAvail.price !== undefined
    ? Number(firstAvail.price)
    : (firstAvail.unitCost !== null && firstAvail.unitCost !== undefined ? Number(firstAvail.unitCost) : null);
}

/**
 * Get all product prices by location from Availability.price field.
 */
export function getProductPrices(availabilities?: Array<{
  covaLocationId: number;
  price?: number | null;
  unitCost?: number | null;
}>): Array<{
  id: string;
  covaLocationId: number;
  price: number;
}> {
  if (!availabilities || !Array.isArray(availabilities)) return [];

  // Group by location and get first price per location
  const priceMap = new Map<number, number>();
  for (const avail of availabilities) {
    if (!priceMap.has(avail.covaLocationId)) {
      const price = avail.price !== null && avail.price !== undefined
        ? Number(avail.price)
        : (avail.unitCost !== null && avail.unitCost !== undefined ? Number(avail.unitCost) : 0);
      priceMap.set(avail.covaLocationId, price);
    }
  }

  return Array.from(priceMap.entries()).map(([covaLocationId, price], idx) => ({
    id: `price-${covaLocationId}-${idx}`,
    covaLocationId,
    price,
  }));
}

// ============================================================================
// Order Helpers
// ============================================================================

/**
 * Get order lines from either JSON field or legacy relation
 */
export function getOrderLines(order: {
  lines?: any;
  linesLegacy?: Array<{
    name: string;
    catalogItemId: string;
    quantity: number;
    lineDollarAmount?: number | null;
    packageId?: string | null;
  }>;
}): Array<{
  name: string;
  catalogItemId: string;
  quantity: number;
  lineDollarAmount?: number;
  packageId?: string | null;
}> {
  // Prefer JSON field if available
  if (order.lines && Array.isArray(order.lines)) {
    return order.lines.map((l: any) => ({
      name: l.name,
      catalogItemId: l.catalogItemId,
      quantity: l.quantity,
      lineDollarAmount: l.lineDollarAmount ?? undefined,
      packageId: l.packageId ?? undefined,
    }));
  }
  // Fallback to legacy relation
  if (order.linesLegacy && Array.isArray(order.linesLegacy)) {
    return order.linesLegacy.map(l => ({
      name: l.name,
      catalogItemId: l.catalogItemId,
      quantity: l.quantity,
      lineDollarAmount: l.lineDollarAmount ?? undefined,
      packageId: l.packageId ?? undefined,
    }));
  }
  return [];
}

// ============================================================================
// Admin Permission Helpers
// ============================================================================

/**
 * Get admin permissions from either JSON field or legacy relation
 */
export function getAdminPermissions(adminUser: {
  permissionsJson?: any;
  permissions?: Array<{
    module: string;
    canRead: boolean;
    canWrite: boolean;
    canDelete: boolean;
  }>;
}): Array<{
  module: string;
  canRead: boolean;
  canWrite: boolean;
  canDelete: boolean;
}> {
  // Prefer JSON field if available
  if (adminUser.permissionsJson && Array.isArray(adminUser.permissionsJson)) {
    return adminUser.permissionsJson as any[];
  }
  // Fallback to legacy relation
  if (adminUser.permissions && Array.isArray(adminUser.permissions)) {
    return adminUser.permissions.map(p => ({
      module: p.module,
      canRead: p.canRead,
      canWrite: p.canWrite,
      canDelete: p.canDelete,
    }));
  }
  return [];
}
